/**
 * SEO utility functions for generating meta tags and structured data
 */

export interface SEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  ogImage?: string;
  businessName?: string;
  businessSlug?: string;
  meetingName?: string;
  duration?: number;
  meetingClassification?: string;
}

export interface StructuredData {
  "@context": string;
  "@type": string;
  name: string;
  description: string;
  provider?: {
    "@type": string;
    name: string;
    url?: string;
  };
  offers?: {
    "@type": string;
    availability: string;
    duration?: string;
    price?: string;
    priceCurrency?: string;
  };
  url?: string;
  image?: string;
}

/**
 * Generate enhanced SEO description for booking pages
 */
export function generateBookingDescription(
  meetingName: string,
  businessName: string,
  duration: number,
  description?: string,
  bookingMode: 'new' | 'reschedule' | 'cancel' = 'new'
): string {
  const baseDescription = description || `Book a ${duration} minute meeting with ${businessName}`;
  
  switch (bookingMode) {
    case 'reschedule':
      return `Reschedule your ${duration} minute meeting with ${businessName}. Find the perfect time that aligns with your energy levels.`;
    case 'cancel':
      return `Cancel your ${duration} minute meeting with ${businessName}.`;
    default:
      return `${baseDescription}. Schedule at your optimal energy time using ChronoCal's biologically intelligent scheduler that works with your circadian rhythm.`;
  }
}

/**
 * Generate page title for booking pages
 */
export function generateBookingTitle(
  meetingName: string,
  businessName: string,
  bookingMode: 'new' | 'reschedule' | 'cancel' = 'new'
): string {
  switch (bookingMode) {
    case 'reschedule':
      return `Reschedule ${meetingName}`;
    case 'cancel':
      return `Cancel ${meetingName}t`;
    default:
      return `${meetingName} | ChronoCal`;
  }
}

/**
 * Generate keywords for booking pages
 */
export function generateBookingKeywords(
  businessName: string,
  meetingName: string,
  meetingClassification?: string
): string {
  const baseKeywords = [
    'scheduling',
    'booking',
    'meeting',
    businessName,
    meetingName,
    'calendar',
    'chronobiology',
    'energy optimization',
    'productivity'
  ];
  
  if (meetingClassification) {
    baseKeywords.push(meetingClassification);
  }
  
  return baseKeywords.join(', ');
}

/**
 * Generate structured data for booking pages
 */
export function generateBookingStructuredData(data: SEOData): StructuredData {
  const {
    title,
    description,
    canonicalUrl,
    ogImage,
    businessName,
    businessSlug,
    meetingName,
    duration
  } = data;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: meetingName || title,
    description,
    provider: businessName ? {
      "@type": "Organization",
      name: businessName,
      url: businessSlug ? `https://chronocal.io/book/${businessSlug}` : undefined
    } : undefined,
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      duration: duration ? `PT${duration}M` : undefined,
      price: "0",
      priceCurrency: "USD"
    },
    url: canonicalUrl,
    image: ogImage
  };
}

/**
 * Get default Open Graph image
 */
export function getDefaultOGImage(): string {
  return "https://chronocal.io/assets/images/ui/b5ba0db6-c39f-4016-9785-3b5fe0bbfaaa.png";
}

/**
 * Generate complete SEO data for booking pages
 */
export function generateBookingSEOData(
  meetingLink: any,
  businessName: string,
  businessSlug: string,
  bookingMode: 'new' | 'reschedule' | 'cancel' = 'new',
  canonicalUrl: string
): SEOData {
  const meetingName = meetingLink.name || "Meeting";
  const duration = meetingLink.duration;
  const description = generateBookingDescription(
    meetingName,
    businessName,
    duration,
    meetingLink.description,
    bookingMode
  );
  const title = generateBookingTitle(meetingName, businessName, bookingMode);
  const keywords = generateBookingKeywords(
    businessName,
    meetingName,
    meetingLink.meeting_classification
  );
  // Booking pages: use the dedicated Twitter/OG image asset for consistent previews.
  const ogImage = "https://chronocal.io/assets/images/og/twitter-og.jpg";

  return {
    title,
    description,
    keywords: keywords.split(', '),
    canonicalUrl,
    ogImage,
    businessName,
    businessSlug,
    meetingName,
    duration,
    meetingClassification: meetingLink.meeting_classification
  };
}
